import { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Flame, X } from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';
import * as Haptics from 'expo-haptics';

const MOTIVATIONAL_QUOTES = [
  "The iron never lies. Time to get after it!",
  "Every rep brings you closer to your goals!",
  "Champions are made when no one is watching.",
  "Your only limit is you. Destroy it today!",
  "Pain is temporary. Pride is forever.",
  "The best project you'll ever work on is you.",
  "Sweat is just fat crying. Make it weep!",
  "Push yourself because no one else will.",
  "Strong is the new beautiful.",
  "Your body can stand almost anything. It's your mind you have to convince.",
  "Don't wish for it. Work for it.",
  "The only bad workout is the one that didn't happen.",
  "Make yourself proud today.",
  "Dedication sees dreams come true.",
  "No pain, no gain. Shut up and train!",
];

export default function DailyMotivation() {
  const { profile } = useAuth();
  const { theme } = useTheme();
  const [visible, setVisible] = useState(false);
  const [quote, setQuote] = useState('');

  useEffect(() => {
    checkDailyMotivation();
  }, [profile]);

  const checkDailyMotivation = async () => {
    if (!profile) return;

    const today = new Date().toDateString();
    const lastSeen = profile.last_motivation_seen
      ? new Date(profile.last_motivation_seen).toDateString()
      : null;

    const isMotivationEnabled = profile.daily_motivation_enabled !== false;

    if (isMotivationEnabled && today !== lastSeen) {
      // Deterministic daily rotation based on day-of-year
      const now = new Date();
      const dayOfYear = Math.floor((now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) / 86400000);
      const randomQuote = MOTIVATIONAL_QUOTES[dayOfYear % MOTIVATIONAL_QUOTES.length];
      setQuote(randomQuote);
      setVisible(true);
    }
  };

  const handleClose = async () => {
    if (Platform.OS !== 'web') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }

    await supabase
      .from('profiles')
      .update({ last_motivation_seen: new Date().toISOString() })
      .eq('id', profile!.id);

    setVisible(false);
  };

  if (!visible) return null;

  return (
    <Modal visible={visible} animationType="fade" transparent>
      <View style={[styles.overlay, { backgroundColor: 'rgba(0,0,0,0.85)' }]}>
        <View style={[styles.container, { backgroundColor: theme.colors.surface, shadowColor: theme.colors.background, shadowOffset: { width: 0, height: 10 }, shadowOpacity: 0.5, shadowRadius: 20 }]}>
          <LinearGradient
            colors={[theme.colors.accent + '20', theme.colors.surface]}
            style={styles.gradientBg}
          />

          <TouchableOpacity style={styles.closeButton} onPress={handleClose}>
            <X size={24} color={theme.colors.textSecondary} />
          </TouchableOpacity>

          <View style={styles.iconContainer}>
            <Flame size={64} color={theme.colors.accent} strokeWidth={2} />
          </View>

          <Text style={[styles.title, { color: theme.colors.accent }]}>
            DAILY MOTIVATION
          </Text>

          <Text style={[styles.quote, { color: theme.colors.text }]}>
            &quot;{quote}&quot;
          </Text>

          <TouchableOpacity
            style={[styles.button, { backgroundColor: theme.colors.accent, shadowColor: theme.colors.background, shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.3, shadowRadius: 8 }]}
            onPress={handleClose}>
            <Text style={[styles.buttonText, { color: theme.colors.accentForeground }]}>
              LET&apos;S GO!
            </Text>
          </TouchableOpacity>

          <Text style={[styles.note, { color: theme.colors.textTertiary }]}>
            You can disable these messages in Profile Settings
          </Text>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  container: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 2,
    padding: 32,
    alignItems: 'center',
    elevation: 10,
  },
  gradientBg: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 150,
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 36,
    height: 36,
    borderRadius: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  iconContainer: {
    marginBottom: 20,
    zIndex: 5,
  },
  title: {
    fontSize: 20,
    fontWeight: '900',
    letterSpacing: 2,
    marginBottom: 24,
    zIndex: 5,
  },
  quote: {
    fontSize: 18,
    fontWeight: '600',
    lineHeight: 28,
    textAlign: 'center',
    marginBottom: 32,
    fontStyle: 'italic',
  },
  button: {
    paddingVertical: 16,
    paddingHorizontal: 48,
    borderRadius: 0,
    marginBottom: 16,
    elevation: 5,
  },
  buttonText: {
    fontSize: 18,
    fontWeight: '900',
    letterSpacing: 1,
  },
  note: {
    fontSize: 12,
    textAlign: 'center',
  },
});
